import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";

const COLORS = {
  emerald: "#E9B934",
  gold: "#F6C93A",
  sky: "#38BDF8",
  red: "#EF4444",
  violet: "#A855F7",
  pink: "#EC4899",
};

const DashTooltip = ({ active, payload, label, prefix = "" }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-black/80 backdrop-blur-md border border-white/10 rounded-md px-3 py-2 text-xs font-mono">
      <div className="text-neutral-400 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-neutral-300">{p.name}:</span>
          <span className="text-white">
            {prefix}
            {Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const SimpleLineChart = ({
  data,
  dataKey = "value",
  xKey = "date",
  color = COLORS.emerald,
  height = 220,
  prefix = "",
  testid,
}) => {
  return (
    <div className="w-full" style={{ height }} data-testid={testid || "line-chart"}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 6, right: 6, left: 6, bottom: 6 }}>
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis
            dataKey={xKey}
            stroke="#4B5563"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
          />
          <YAxis
            stroke="#4B5563"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={(v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          />
          <Tooltip content={<DashTooltip prefix={prefix} />} />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            fill={`url(#grad-${dataKey})`}
            isAnimationActive={true}
            animationDuration={900}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const MultiLineChart = ({ series = [], years, height = 320, testid }) => {
  // series: [{ label, color, data: [{year, value}]}]
  const colors = [COLORS.emerald, COLORS.gold, COLORS.sky, COLORS.violet, COLORS.pink, COLORS.red];
  const merged = {};
  series.forEach((s, idx) => {
    s._color = s.color || colors[idx % colors.length];
    s.data.forEach((p) => {
      merged[p.year] = merged[p.year] || { year: p.year };
      merged[p.year][s.label] = p.value;
    });
  });
  const data = Object.values(merged).sort((a, b) => a.year - b.year);

  return (
    <div className="w-full" style={{ height }} data-testid={testid || "multi-chart"}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 6, right: 6, left: 6, bottom: 6 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis
            dataKey="year"
            stroke="#4B5563"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
            label={{
              value: `${years ?? ""}`.trim(),
              fill: "#4B5563",
              fontSize: 10,
              position: "insideBottomRight",
            }}
          />
          <YAxis
            stroke="#4B5563"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={false}
            width={70}
            tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          />
          <Tooltip content={<DashTooltip prefix="$" />} />
          {series.map((s) => (
            <Line
              key={s.label}
              type="monotone"
              dataKey={s.label}
              stroke={s._color}
              strokeWidth={2.2}
              dot={false}
              isAnimationActive={true}
              animationDuration={900}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export { COLORS };
